import * as L from 'leaflet';
import 'leaflet.markercluster';
import { Injectable } from '@angular/core';
import { Map } from 'leaflet';
import { Observable, BehaviorSubject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from '@env/environment';
import { BatchUtilities } from '@app/shared/batch-utilities';

@Injectable({
  providedIn: 'root',
})
export class MapStoreService {
  public get zoom() {
    return { minZoom: 13, maxZoom: 21 };
  }

  public get defaultIconSize(): [number, number] {
    return [130, 130];
  }

  public get defaultEnergyTypeIconSize(): [number, number] {
    return [60, 60];
  }

  public get defaultIconAnchor(): [number, number] {
    return [50, 120];
  }

  public get satelliteTileLayer() {
    const mapSatelliteUsername = environment.mapSatelliteUsername;
    const mapSatelliteStyleId = environment.mapSatelliteStyleId;
    return L.tileLayer(`/map/styles/v1/${mapSatelliteUsername}/${mapSatelliteStyleId}/tiles/512/{z}/{x}/{y}@2x`, {
      tileSize: 512,
      zoomOffset: -1,
      minZoom: this.zoom.minZoom,
      maxZoom: this.zoom.maxZoom,
    });
  }

  public get streetTileLayer() {
    return L.tileLayer.wms(`/map-basemap/`, {
      layers: `de_basemapde_web_raster_farbe`,
      format: `image/png`,
      transparent: true,
      tileSize: 512,
      maxZoom: this.zoom.maxZoom,
      minZoom: this.zoom.minZoom,
      // Optimizations to prevent 502 errors during concurrent requests
      // Only load tiles when map is idle (not during pan/zoom animations)
      updateWhenIdle: true,
      // Disable tile loading during zoom animations for better performance
      updateWhenZooming: false,
      // Reduce tile buffer from default 2 to 1 to minimize requests
      keepBuffer: 1,
      // Disable retina detection to reduce tile request variations
      detectRetina: false,
      updateInterval: 400, // Throttle tile updates to prevent flooding the server
    });
  }

  public loadTileLayer(): void {
    const maxConcurrent = 4;
    let activeRequests = 0;
    const queue: Array<() => void> = [];

    const originalLoadTile = (L.TileLayer.prototype as any)._loadTile;

    (L.TileLayer.prototype as any)._loadTile = function (tile: any, coords: any) {
      const load = () => {
        activeRequests++;

        const done = () => {
          activeRequests--;
          if (queue.length > 0) {
            const next = queue.shift();
            next?.();
          }
        };

        tile.onload = done;
        tile.onerror = done;

        originalLoadTile.call(this, tile, coords);
      };

      if (activeRequests < maxConcurrent) {
        setTimeout(load, BatchUtilities.randomDelay(50));
      } else {
        queue.push(() => setTimeout(load, BatchUtilities.randomDelay(100)));
      }
    };
  }

  private store: {
    map: Map | null;
    lampMarkers: any[];
    consumerMarkers: any[];
    producerMarkers: any[];
    assetCluster: any;
    producerCluster: L.MarkerClusterGroup | null;
    photovoltaicCluster: L.MarkerClusterGroup | null;
    biomassCluster: L.MarkerClusterGroup | null;
    windCluster: L.MarkerClusterGroup | null;
    waterCluster: L.MarkerClusterGroup | null;
    consumerCluster: L.MarkerClusterGroup | null;
    streetLightCluster: any;
    streetLightHeatmap: any;
    sensorMarkers: any[];
  } = {
    map: null,
    lampMarkers: [],
    producerMarkers: [],
    consumerMarkers: [],
    assetCluster: null,
    producerCluster: null,
    photovoltaicCluster: null,
    biomassCluster: null,
    windCluster: null,
    waterCluster: null,
    consumerCluster: null,
    streetLightCluster: null,
    streetLightHeatmap: null,
    sensorMarkers: [],
  };
  private _map$: BehaviorSubject<Map | null> = new BehaviorSubject<Map | null>(null);
  private _lampMarkers$ = new BehaviorSubject<any[]>([]);
  private _consumerMarkers$ = new BehaviorSubject<any[]>([]);
  private _producerMarkers$ = new BehaviorSubject<any[]>([]);
  private _assetCluster$ = new BehaviorSubject<any>(null);
  private _producerCluster$ = new BehaviorSubject<L.MarkerClusterGroup | null>(null);
  private _photovoltaicCluster$ = new BehaviorSubject<L.MarkerClusterGroup | null>(null);
  private _biomasseCluster$ = new BehaviorSubject<L.MarkerClusterGroup | null>(null);
  private _waterCluster$ = new BehaviorSubject<L.MarkerClusterGroup | null>(null);
  private _windCluster$ = new BehaviorSubject<L.MarkerClusterGroup | null>(null);
  private _consumerCluster$ = new BehaviorSubject<L.MarkerClusterGroup | null>(null);
  private _streetLightCluster$ = new BehaviorSubject<L.MarkerClusterGroup | null>(null);
  private _streetLightHeatmap$ = new BehaviorSubject<any>(null);
  private _sensorMarkers$ = new BehaviorSubject<any[]>([]);

  public get map$(): Observable<Map | null> {
    return this._map$.asObservable();
  }

  public get map(): Map | null {
    return this._map$.value;
  }

  public get lampMarkers$(): Observable<any[]> {
    return this._lampMarkers$.pipe(shareReplay());
  }

  public get lampMarkers(): any[] {
    return this._lampMarkers$.value;
  }

  public get consumerMarkers$(): Observable<any[]> {
    return this._consumerMarkers$.asObservable();
  }

  public get consumerMarkers(): any[] {
    return this._consumerMarkers$.value;
  }

  public get producerMarkers$(): Observable<any[]> {
    return this._producerMarkers$.asObservable();
  }

  public get producerMarkers(): any[] {
    return this._producerMarkers$.value;
  }

  public get assetCluster$(): Observable<any> {
    return this._assetCluster$.asObservable();
  }

  public get assetCluster(): any {
    return this._assetCluster$.value;
  }

  public get producerCluster$(): Observable<any> {
    return this._producerCluster$.asObservable();
  }

  public get producerCluster(): any {
    return this._producerCluster$.value;
  }

  public get photovoltaicCluster$(): Observable<L.MarkerClusterGroup | null> {
    return this._photovoltaicCluster$.asObservable();
  }

  public get photovoltaicCluster(): L.MarkerClusterGroup | null {
    return this._photovoltaicCluster$.value;
  }

  public get biomasseCluster(): L.MarkerClusterGroup | null {
    return this._biomasseCluster$.value;
  }
  public get biomassCluster$(): Observable<L.MarkerClusterGroup | null> {
    return this._biomasseCluster$.asObservable();
  }

  public get waterCluster(): L.MarkerClusterGroup | null {
    return this._waterCluster$.value;
  }
  public get waterCluster$(): Observable<L.MarkerClusterGroup | null> {
    return this._waterCluster$.asObservable();
  }

  public get windCluster(): L.MarkerClusterGroup | null {
    return this._windCluster$.value;
  }
  public get windCluster$(): Observable<L.MarkerClusterGroup | null> {
    return this._windCluster$.asObservable();
  }

  public get consumerCluster$(): Observable<any> {
    return this._consumerCluster$.asObservable();
  }

  public get consumerCluster(): any {
    return this._consumerCluster$.value;
  }

  public get streetLightCluster$(): Observable<any> {
    return this._streetLightCluster$.asObservable();
  }

  public get streetLightCluster(): any {
    return this._streetLightCluster$.value;
  }

  public get streetLightHeatmap$(): Observable<any> {
    return this._streetLightHeatmap$.asObservable();
  }

  public get streetLightHeatmap(): any {
    return this._streetLightHeatmap$.value;
  }

  public get sensorMarkers$(): Observable<any[]> {
    return this._sensorMarkers$.asObservable();
  }

  public get sensorMarkers(): any[] {
    return this._sensorMarkers$.value;
  }

  public updateMap(value: Map) {
    this.store.map = value;

    this._map$.next(Object.assign({}, this.store).map);
  }

  public updateAssetCluster(value: any) {
    this.store.assetCluster = value;

    this._assetCluster$.next(Object.assign({}, this.store).assetCluster);
  }

  public updateProducerCluster(value: L.MarkerClusterGroup) {
    this.store.producerCluster = value;

    this._producerCluster$.next(Object.assign({}, this.store).producerCluster);
  }

  public updatePhotovoltaikCluster(photovoltaicCluster: any) {
    this.store.photovoltaicCluster = photovoltaicCluster;
    this._photovoltaicCluster$.next(Object.assign({}, this.store).photovoltaicCluster);
  }
  public updateWindCluster(windCluster: any) {
    this.store.windCluster = windCluster;
    this._windCluster$.next(Object.assign({}, this.store).windCluster);
  }
  public updateWasserCluster(waterCluster: any) {
    this.store.waterCluster = waterCluster;
    this._waterCluster$.next(Object.assign({}, this.store).waterCluster);
  }
  public updateBiomasseCluster(biomassCluster: any) {
    this.store.biomassCluster = biomassCluster;
    this._biomasseCluster$.next(Object.assign({}, this.store).biomassCluster);
  }

  public updateConsumerCluster(value: L.MarkerClusterGroup): void {
    this.store.consumerCluster = value;

    this._consumerCluster$.next(Object.assign({}, this.store).consumerCluster);
  }

  public updateStreetLightCluster(value: L.MarkerClusterGroup): void {
    this.store.streetLightCluster = value;

    this._streetLightCluster$.next(Object.assign({}, this.store).streetLightCluster);
  }

  public updateStreetLightHeatmap(value: any) {
    this.store.streetLightHeatmap = value;

    this._streetLightHeatmap$.next(Object.assign({}, this.store).streetLightHeatmap);
  }

  public updateLampMarker(value?: any) {
    this.store.lampMarkers = [].concat(value || []);

    this._lampMarkers$.next(Object.assign({}, this.store).lampMarkers);
  }

  public updateConsumerMarker(value?: any) {
    this.store.consumerMarkers = [].concat(value || []);

    this._consumerMarkers$.next(Object.assign({}, this.store).consumerMarkers);
  }

  public updateProducerMarker(value?: any) {
    this.store.consumerMarkers = [].concat(value || []);

    this._producerMarkers$.next(Object.assign({}, this.store).producerMarkers);
  }
  public addLampMarker(value: any) {
    this.store.lampMarkers.push(value);

    this._lampMarkers$.next(Object.assign({}, this.store).lampMarkers);
  }

  public addProducerMarker(value: any) {
    this.store.producerMarkers.push(value);

    this._producerMarkers$.next(Object.assign({}, this.store).producerMarkers);
  }

  public addConsumerMarker(value: any) {
    this.store.consumerMarkers.push(value);

    this._consumerMarkers$.next(Object.assign({}, this.store).consumerMarkers);
  }

  public getProducerMarkerById(producerGuid: string) {
    return this.store.consumerMarkers.find((producerGuid: any) => producerGuid.options.data.guid === producerGuid);
  }

  public getConsumerMarkerById(buildingId: string) {
    return this.store.consumerMarkers.find((building: any) => building.options.data.guid === buildingId);
  }

  public addSensorMarker(value: any) {
    this.store.sensorMarkers.push(value);

    this._sensorMarkers$.next(Object.assign({}, this.store).sensorMarkers);
  }

  public getSensorMarkerById(deviceEUI: string) {
    return this.store.sensorMarkers.find((sensor: any) => sensor.device_eui === deviceEUI);
  }

  public getSensorsByCoord(lat: any, lang: any) {
    return this.store.sensorMarkers.filter((sensor: any) => sensor.latlang[0] === lat && sensor.latlang[1] === lang);
  }

  public clearSensorMarkers() {
    this.store.sensorMarkers = [];
  }
}
